import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Trash2, Users, ChevronDown, ChevronUp,
  AlertCircle, Send, Crown, SmilePlus, RefreshCw
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { getProfileImageUrl } from '@/lib/utils';
import MentionTextarea, { MentionText } from '../MentionTextarea';

const API_URL = window.location.origin;

const REACTIONS = ['❤️', '🎉', '🤝🏾', '💡', '🤔', '👏🏾', '💪🏾', '🙌🏾', '🙏🏾', '💎', '🔥', '✨'];

const POST_AS_OPTIONS = [
  { value: 'self', label: 'Post as Myself (Admin)' },
  { value: 'crystal_khalil', label: 'Post as Crystal Khalil', founder: true },
  { value: 'dr_nicole_labeach', label: 'Post as Dr. Nicole LaBeach', founder: true },
];

const AdminCircleModeration = () => {
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCircle, setExpandedCircle] = useState(null);
  const [circleData, setCircleData] = useState({});
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Compose state per circle
  const [composeText, setComposeText] = useState('');
  const [postAs, setPostAs] = useState('self');
  const [sending, setSending] = useState(false);
  const [showReactions, setShowReactions] = useState(null);

  // Add member state
  const [showAddMember, setShowAddMember] = useState(null);
  const [addMemberEmail, setAddMemberEmail] = useState('');
  // Member actions popover state: { circleId, member }
  const [memberAction, setMemberAction] = useState(null);
  const [memberActionBusy, setMemberActionBusy] = useState(false);
  const [addingMember, setAddingMember] = useState(false);

  // Sync memberships state
  const [syncing, setSyncing] = useState(false);
  // Reconcile counts state
  const [reconciling, setReconciling] = useState(false);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchCircles();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const handleAddMember = async (circleId) => {
    if (!addMemberEmail.trim()) return;
    setAddingMember(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/circles/${circleId}/add-member`,
        { email: addMemberEmail.trim() },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message);
      setAddMemberEmail('');
      setShowAddMember(null);
      fetchMessages(circleId);
      fetchCircles();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Failed to add member: ${detail}`);
    } finally {
      setAddingMember(false);
    }
  };

  // ------- Admin: Move sister to another circle -------
  const handleMoveMember = async (member, fromCircleId) => {
    // Build the list of OTHER active circles as options
    const others = circles.filter((c) => c.id !== fromCircleId && c.status === 'active');
    if (others.length === 0) {
      toast.error('No other active circles available to move this sister to.');
      return;
    }
    const choice = window.prompt(
      `Move ${member.full_name} to which circle? Enter the number:\n\n` +
        others.map((c, i) => `${i + 1}. ${c.name} (${c.actual_member_count ?? c.member_ids?.length ?? 0} members)`).join('\n')
    );
    if (!choice) return;
    const idx = parseInt(choice, 10) - 1;
    if (isNaN(idx) || idx < 0 || idx >= others.length) {
      toast.error('Invalid selection.');
      return;
    }
    const targetCircle = others[idx];
    setMemberActionBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/circles/${targetCircle.id}/add-member`,
        { email: member.email, mode: 'move' },
        { headers: getAuthHeaders() }
      );
      toast.success(`${member.full_name} moved to ${targetCircle.name}. She'll receive an email letting her know.`);
      setMemberAction(null);
      fetchCircles();
    } catch (err) {
      toast.error(`Move failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setMemberActionBusy(false);
    }
  };

  const handleRemoveMember = async (member, circleId) => {
    if (!window.confirm(
      `Remove ${member.full_name} from this circle?\n\nShe'll stay in any other circles she belongs to.`
    )) return;
    setMemberActionBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/circles/${circleId}/remove-member`,
        { user_id: member.id },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message);
      setMemberAction(null);
      fetchCircles();
    } catch (err) {
      toast.error(`Remove failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setMemberActionBusy(false);
    }
  };

  const handleSyncMemberships = async () => {
    if (!window.confirm(
      'This will repair circle memberships:\n' +
      '• Move any users stuck in the legacy field\n' +
      '• Remove orphaned IDs (deleted users)\n' +
      '• Recalculate member counts\n\n' +
      'Safe to run multiple times. Continue?'
    )) return;
    setSyncing(true);
    try {
      const res = await axios.get(`${API_URL}/api/debug/fix-circle-membership-schema`);
      const updated = res.data?.circles_updated ?? 0;
      const details = res.data?.details || [];
      if (updated === 0) {
        toast.success('All circle memberships are already in sync.');
      } else {
        const totalOrphans = details.reduce((sum, d) => sum + (d.removed_orphans?.length || 0), 0);
        const totalAdded = details.reduce((sum, d) => sum + (d.added_to_member_ids?.length || 0), 0);
        const parts = [`Repaired ${updated} circle${updated === 1 ? '' : 's'}`];
        if (totalAdded) parts.push(`+${totalAdded} added`);
        if (totalOrphans) parts.push(`-${totalOrphans} orphans removed`);
        toast.success(parts.join(' · '));
      }
      fetchCircles();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Sync failed: ${detail}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleReconcileCounts = async () => {
    if (!window.confirm(
      'Walk every accountability circle and refresh the displayed "member count" so it matches the real number of members.\n\n' +
      'This fixes stale labels like "11 sisters supporting each other" when only 1 sister is actually in the circle.\n\n' +
      'Safe to run anytime. Continue?'
    )) return;
    setReconciling(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/accountability-circles/reconcile-counts`,
        {},
        { headers: getAuthHeaders() }
      );
      const n = res.data?.fixed_count ?? 0;
      if (n === 0) {
        toast.success('All circle counts are already accurate.');
      } else {
        const sample = (res.data?.fixed || [])
          .slice(0, 3)
          .map((c) => `${c.name}: ${c.was}→${c.now}`)
          .join(' · ');
        toast.success(`Reconciled ${n} circle${n === 1 ? '' : 's'}. ${sample}`, { duration: 10000 });
      }
      fetchCircles();
    } catch (err) {
      toast.error(`Reconcile failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setReconciling(false);
    }
  };

  const fetchCircles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/accountability-circles`, {
        headers: getAuthHeaders(),
        timeout: 30000,
      });
      setCircles(response.data.circles || []);
    } catch (error) {
      console.error('Error fetching circles:', error.response?.data || error.message);
      if (error.response?.status !== 404) {
        toast.error(`Failed to load circles: ${error.response?.data?.detail || error.message}`);
      }
      setCircles([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (circleId) => {
    setLoadingMessages(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/accountability-circles/${circleId}/messages`,
        { headers: getAuthHeaders() }
      );
      setCircleData((prev) => ({
        ...prev,
        [circleId]: {
          name: response.data.circle_name,
          focusArea: response.data.focus_area,
          members: response.data.members || [],
          messages: response.data.messages || [],
        },
      }));
      setExpandedCircle(circleId);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleDeleteMessage = async (circleId, messageId) => {
    if (!window.confirm('Are you sure you want to delete this message? This cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(
        `${API_URL}/api/admin/accountability-circles/${circleId}/messages/${messageId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Message deleted');
      fetchMessages(circleId);
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleSendMessage = async (circleId) => {
    if (!composeText.trim()) return;
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/accountability-circles/${circleId}/messages`,
        { content: composeText.trim(), message_type: 'text', post_as: postAs },
        { headers: getAuthHeaders() }
      );
      const label = POST_AS_OPTIONS.find((o) => o.value === postAs)?.label || 'Admin';
      toast.success(`Message sent as ${label}`);
      setComposeText('');
      fetchMessages(circleId);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleReact = async (circleId, messageId, reaction) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/accountability-circles/${circleId}/messages/${messageId}/react`,
        { reaction, post_as: postAs },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.toggled === 'on' ? 'Reaction added' : 'Reaction removed');
      setShowReactions(null);
      fetchMessages(circleId);
    } catch (error) {
      toast.error('Failed to react');
    }
  };

  const toggleCircle = (circleId) => {
    if (expandedCircle === circleId) {
      setExpandedCircle(null);
    } else {
      setComposeText('');
      setPostAs('self');
      fetchMessages(circleId);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-circle-moderation">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-800">
            Accountability Circle Moderation
          </h3>
        </div>
        <Button
          onClick={handleSyncMemberships}
          disabled={syncing}
          variant="outline"
          size="sm"
          className="gap-2 border-purple-200 text-purple-700 hover:bg-purple-50"
          data-testid="sync-circle-memberships-btn"
          title="Repair any users stuck in the legacy 'members' field — safe to run anytime."
        >
          <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Circle Memberships'}
        </Button>
        <Button
          onClick={handleReconcileCounts}
          disabled={reconciling}
          variant="outline"
          size="sm"
          className="gap-2 border-amber-200 text-amber-800 hover:bg-amber-50 ml-2"
          data-testid="reconcile-circle-counts-btn"
          title="Refresh stale member counts so the displayed number always matches reality."
        >
          <RefreshCw className={`h-4 w-4 ${reconciling ? 'animate-spin' : ''}`} />
          {reconciling ? 'Reconciling...' : 'Reconcile Counts'}
        </Button>
      </div>

      {circles.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-gray-500">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p>No accountability circles found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {circles.map((circle) => {
            const data = circleData[circle.id];
            const isExpanded = expandedCircle === circle.id;

            return (
              <Card key={circle.id} className="overflow-hidden">
                {/* Circle header */}
                <CardHeader
                  className="cursor-pointer hover:bg-gray-50 transition-colors py-3"
                  onClick={() => toggleCircle(circle.id)}
                  data-testid={`circle-header-${circle.id}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3 flex-wrap min-w-0">
                      <div className={`w-3 h-3 rounded-full shrink-0 ${
                        circle.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                      }`} />
                      <CardTitle className="text-base font-medium">
                        {circle.name}
                      </CardTitle>
                      {(() => {
                        const actual = circle.actual_member_count ?? (circle.member_ids?.length || 0);
                        const stored = circle.member_count ?? actual;
                        const drift = stored !== actual;
                        const active = circle.active_member_count_7d ?? null;
                        return (
                          <>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              drift
                                ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                : 'bg-gray-100 text-gray-600'
                            }`} title={drift ? `Stored count: ${stored}, actual: ${actual}. Click "Reconcile Counts".` : ''}>
                              {actual} member{actual === 1 ? '' : 's'}
                              {drift && ` · stale (${stored})`}
                            </span>
                            {active !== null && actual > 0 && (
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full ${
                                  active === 0
                                    ? 'bg-red-50 text-red-700 border border-red-200'
                                    : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                }`}
                                title="Sisters who posted, checked in, or reacted in the last 7 days"
                              >
                                {active} active 7d
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className="text-xs">
                        {circle.focus_area}
                      </Badge>
                      {isExpanded
                        ? <ChevronUp className="h-5 w-5 text-gray-400" />
                        : <ChevronDown className="h-5 w-5 text-gray-400" />}
                    </div>
                  </div>
                  {/* Inline member roster preview (always visible) */}
                  {circle.member_emails?.length > 0 && (
                    <div className="mt-2 ml-6 text-xs text-gray-500 truncate" title={circle.member_emails.join(', ')}>
                      Members: {circle.member_emails.slice(0, 4).join(', ')}
                      {circle.member_emails.length > 4 ? ` +${circle.member_emails.length - 4} more` : ''}
                    </div>
                  )}
                </CardHeader>

                {/* Expanded circle content */}
                {isExpanded && (
                  <CardContent className="border-t p-0">
                    {loadingMessages ? (
                      <div className="flex items-center justify-center p-6">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600" />
                      </div>
                    ) : (
                      <div className="flex flex-col" style={{ maxHeight: '70vh' }}>
                        {/* Members bar */}
                        {data?.members?.length > 0 && (() => {
                          // Map admin-list activity data to each member by id so we can
                          // show a tiny "active last 7 days" dot next to each avatar.
                          const activeById = {};
                          (circle.members_detailed || []).forEach((m) => {
                            activeById[m.id] = !!m.active_last_7_days;
                          });
                          const activeCount = circle.active_member_count_7d ?? 0;
                          return (
                            <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b overflow-x-auto">
                              <span className="text-xs font-medium text-gray-500 shrink-0">
                                Members
                                <span className="ml-2 text-emerald-700">
                                  · {activeCount} active in last 7d
                                </span>
                                :
                              </span>
                              {data.members.map((m) => {
                                const isActive = !!activeById[m.id];
                                const memberWithEmail = (circle.members_detailed || []).find((md) => md.id === m.id);
                                const memberObj = { ...m, email: memberWithEmail?.email };
                                return (
                                  <div key={m.id} className="flex items-center gap-1 shrink-0 group">
                                    <button
                                      type="button"
                                      onClick={() => setMemberAction({ circleId: circle.id, member: memberObj })}
                                      className="relative cursor-pointer focus:outline-none rounded-full"
                                      title={`${m.full_name} — click for actions`}
                                      data-testid={`member-action-trigger-${m.id}`}
                                    >
                                      <Avatar className="h-6 w-6 ring-1 ring-transparent group-hover:ring-forest/40 transition-all">
                                        <AvatarImage src={getProfileImageUrl(m.profile_image)} />
                                        <AvatarFallback className="text-[10px] bg-purple-100 text-purple-700">
                                          {getInitials(m.full_name)}
                                        </AvatarFallback>
                                      </Avatar>
                                      <span
                                        className={`absolute -bottom-0.5 -right-0.5 block h-2 w-2 rounded-full ring-2 ring-gray-50 ${
                                          isActive ? 'bg-emerald-500' : 'bg-gray-300'
                                        }`}
                                        title={isActive ? 'Active in last 7 days' : 'No activity in last 7 days'}
                                        data-testid={`member-activity-dot-${m.id}`}
                                      />
                                    </button>
                                    <span className="text-xs text-gray-600">{m.full_name?.split(' ')[0]}</span>
                                  </div>
                                );
                              })}
                              <button
                                onClick={() => setShowAddMember(showAddMember === circle.id ? null : circle.id)}
                                className="shrink-0 ml-1 w-6 h-6 rounded-full bg-forest/10 text-forest flex items-center justify-center hover:bg-forest/20 transition-colors text-xs font-bold"
                                title="Add Member"
                                data-testid={`add-member-btn-${circle.id}`}
                              >+</button>
                            </div>
                          );
                        })()}

                        {/* Add member inline form */}
                        {showAddMember === circle.id && (
                          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b">
                            <input
                              type="email"
                              placeholder="Enter member's email..."
                              value={addMemberEmail}
                              onChange={(e) => setAddMemberEmail(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddMember(circle.id)}
                              className="flex-1 text-sm px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-forest/30"
                              data-testid="add-member-email-input"
                              autoFocus
                            />
                            <button
                              onClick={() => handleAddMember(circle.id)}
                              disabled={addingMember || !addMemberEmail.trim()}
                              className="text-xs px-3 py-1.5 bg-forest text-white rounded-lg disabled:opacity-40 hover:bg-forest/90"
                              data-testid="add-member-submit"
                            >
                              {addingMember ? 'Adding...' : 'Add'}
                            </button>
                            <button
                              onClick={() => { setShowAddMember(null); setAddMemberEmail(''); }}
                              className="text-xs text-gray-400 hover:text-gray-600"
                            >Cancel</button>
                          </div>
                        )}

                        {/* Messages feed - scrollable */}
                        <div className="flex-1 overflow-y-auto px-4 py-3" style={{ maxHeight: '50vh', minHeight: '200px' }}>
                          {(!data?.messages || data.messages.length === 0) ? (
                            <p className="text-sm text-gray-500 text-center py-8">
                              No messages in this circle yet. Be the first to post!
                            </p>
                          ) : (
                            <div className="space-y-3">
                              {data.messages.map((message) => (
                                <MessageBubble
                                  key={message.id}
                                  message={message}
                                  circleId={circle.id}
                                  onDelete={handleDeleteMessage}
                                  onReact={handleReact}
                                  showReactions={showReactions}
                                  setShowReactions={setShowReactions}
                                  formatDate={formatDate}
                                  getInitials={getInitials}
                                />
                              ))}
                              <div ref={messagesEndRef} />
                            </div>
                          )}
                        </div>

                        {/* Compose area */}
                        <div className="border-t bg-white px-4 py-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <Select value={postAs} onValueChange={setPostAs}>
                              <SelectTrigger
                                className="w-[260px] h-8 text-xs"
                                data-testid="post-as-selector"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {POST_AS_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    <span className="flex items-center gap-1">
                                      {opt.founder && <Crown className="h-3 w-3 text-amber-500" />}
                                      {opt.label}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {postAs !== 'self' && (
                              <Badge className="bg-amber-100 text-amber-800 text-[10px]">
                                <Crown className="h-3 w-3 mr-1" /> Founder
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-end gap-2">
                            <MentionTextarea
                              value={composeText}
                              onChange={setComposeText}
                              members={data?.members || []}
                              placeholder="Type your message... Use @ to mention members"
                              className="min-h-[60px] max-h-[120px] resize-none text-sm"
                              dataTestId="circle-message-input"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleSendMessage(circle.id);
                                }
                              }}
                            />
                            <Button
                              size="sm"
                              className="h-10 px-4 bg-purple-600 hover:bg-purple-700"
                              disabled={!composeText.trim() || sending}
                              onClick={() => handleSendMessage(circle.id)}
                              data-testid="send-message-btn"
                            >
                              {sending ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Member Action Modal — surfaces "Move to another circle" + "Remove" */}
      {memberAction && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => !memberActionBusy && setMemberAction(null)}
          data-testid="member-action-modal"
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="h-12 w-12">
                <AvatarImage src={getProfileImageUrl(memberAction.member.profile_image)} />
                <AvatarFallback className="bg-purple-100 text-purple-700">
                  {getInitials(memberAction.member.full_name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="font-medium text-forest">{memberAction.member.full_name}</div>
                <div className="text-xs text-gray-500">{memberAction.member.email || ''}</div>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Choose an action for this sister. She'll receive an email any time you move her.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => handleMoveMember(memberAction.member, memberAction.circleId)}
                disabled={memberActionBusy}
                className="w-full justify-start bg-forest hover:bg-forest/90"
                data-testid="member-action-move-btn"
              >
                Move to another circle…
              </Button>
              <Button
                onClick={() => handleRemoveMember(memberAction.member, memberAction.circleId)}
                disabled={memberActionBusy}
                variant="outline"
                className="w-full justify-start border-red-200 text-red-700 hover:bg-red-50"
                data-testid="member-action-remove-btn"
              >
                Remove from this circle
              </Button>
              <Button
                onClick={() => setMemberAction(null)}
                disabled={memberActionBusy}
                variant="ghost"
                className="w-full justify-start text-gray-500"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ───────── Individual message bubble ───────── */
const MessageBubble = ({
  message, circleId, onDelete, onReact,
  showReactions, setShowReactions, formatDate, getInitials,
}) => {
  const isFounder = message.is_founder_post;
  const reactions = message.reactions || [];

  // Group reactions by emoji
  const grouped = reactions.reduce((acc, r) => {
    if (!acc[r.reaction]) acc[r.reaction] = [];
    acc[r.reaction].push(r);
    return acc;
  }, {});

  return (
    <div
      className={`rounded-lg p-3 shadow-sm border ${
        isFounder ? 'bg-amber-50 border-amber-200' : 'bg-white'
      }`}
      data-testid={`message-${message.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarImage src={getProfileImageUrl(message.sender?.profile_image)} />
          <AvatarFallback className={`text-xs ${
            isFounder ? 'bg-amber-200 text-amber-800' : 'bg-purple-100 text-purple-700'
          }`}>
            {getInitials(message.sender?.full_name)}
          </AvatarFallback>
        </Avatar>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-gray-800">
              {message.sender?.full_name || 'Unknown'}
            </span>
            {isFounder && (
              <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0">
                <Crown className="h-2.5 w-2.5 mr-0.5" /> Founder
              </Badge>
            )}
            {message.founder_label && (
              <span className="text-[10px] text-amber-700 font-medium">
                {message.founder_label}
              </span>
            )}
            <span className={`text-xs px-1.5 py-0.5 rounded ${
              message.message_type === 'checkin'
                ? 'bg-green-100 text-green-700'
                : 'bg-blue-100 text-blue-700'
            }`}>
              {message.message_type || 'text'}
            </span>
            <span className="text-xs text-gray-400">
              {formatDate(message.created_at)}
            </span>
          </div>
          <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap break-words">
            <MentionText content={message.content} />
          </p>

          {/* Reaction display */}
          {Object.keys(grouped).length > 0 && (
            <div className="flex items-center gap-1 mt-2 flex-wrap">
              {Object.entries(grouped).map(([emoji, reactors]) => (
                <span
                  key={emoji}
                  className="inline-flex items-center gap-0.5 bg-gray-100 rounded-full px-2 py-0.5 text-xs cursor-default"
                  title={reactors.map((r) => r.reactor_name || 'Someone').join(', ')}
                >
                  {emoji} {reactors.length}
                </span>
              ))}
            </div>
          )}

          {/* Reaction picker */}
          <div className="flex items-center gap-1 mt-1">
            <div className="relative">
              <button
                className="text-gray-400 hover:text-purple-600 transition-colors p-1 rounded hover:bg-purple-50"
                onClick={() =>
                  setShowReactions(showReactions === message.id ? null : message.id)
                }
                data-testid={`react-btn-${message.id}`}
              >
                <SmilePlus className="h-4 w-4" />
              </button>
              {showReactions === message.id && (
                <div className="absolute bottom-full left-0 mb-1 flex items-center gap-1 bg-white border rounded-lg shadow-lg p-1.5 z-10">
                  {REACTIONS.map((r) => (
                    <button
                      key={r}
                      className="hover:scale-125 transition-transform text-base px-1"
                      onClick={() => onReact(circleId, message.id, r)}
                      data-testid={`reaction-${r}-${message.id}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Delete */}
        <Button
          variant="ghost"
          size="sm"
          className="text-red-400 hover:text-red-600 hover:bg-red-50 shrink-0 h-7 w-7 p-0"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(circleId, message.id);
          }}
          data-testid={`delete-message-${message.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default AdminCircleModeration;
